import React from 'react'
import {Modal, Button} from 'react-bootstrap'
import {BranchType} from '../hooks/useConditionNodeDelete'

interface ConditionDeleteModalProps {
  show: boolean
  onHide: () => void
  onConfirm: (branchType: BranchType) => void
  deleteOptions: BranchType[]
}

const ConditionDeleteModal: React.FC<ConditionDeleteModalProps> = ({
  show,
  onHide,
  onConfirm,
  deleteOptions,
}) => {
  const getOptionLabel = (option: BranchType): string => {
    switch (option) {
      case 'yes':
        return 'Delete "Yes" Branch'
      case 'no':
        return 'Delete "No" Branch'
      default:
        return 'Delete Branch'
    }
  }

  const getOptionDescription = (option: BranchType): string => {
    switch (option) {
      case 'yes':
        return 'Remove all nodes and edges in the "yes" branch and merge the "no" branch back into the main path'
      case 'no':
        return 'Remove all nodes and edges in the "no" branch and merge the "yes" branch back into the main path'
      default:
        return 'Delete this branch and all its children'
    }
  }

  const getOptionIcon = (option: BranchType): string => {
    switch (option) {
      case 'yes':
        return 'bi-check-circle text-success'
      case 'no':
        return 'bi-x-circle text-danger'
      default:
        return 'bi-question-circle text-info'
    }
  }

  const getButtonVariant = (option: BranchType): string => {
    switch (option) {
      case 'yes':
        return 'success'
      case 'no':
        return 'danger'
      default:
        return 'primary'
    }
  }

  const handleOptionClick = (branchType: BranchType) => {
    onConfirm(branchType)
    onHide()
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2'>
          <i className='bi bi-trash text-danger fs-4'></i>
          Delete Condition Branch
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='mb-3'>Choose which branch you want to delete:</p>

        <div className='d-flex flex-column gap-3'>
          {deleteOptions.map((option) => (
            <div
              key={option}
              className='border rounded p-3 cursor-pointer hover-bg-light'
              style={{cursor: 'pointer'}}
              onClick={() => handleOptionClick(option)}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
            >
              <div className='d-flex align-items-center gap-3'>
                <div className='flex-shrink-0'>
                  <i className={`bi ${getOptionIcon(option)} fs-3`}></i>
                </div>
                <div className='flex-grow-1'>
                  <h6 className='mb-1 fw-bold'>{getOptionLabel(option)}</h6>
                  <p className='mb-0 text-muted small'>{getOptionDescription(option)}</p>
                </div>
                <div className='flex-shrink-0'>
                  <Button
                    variant={getButtonVariant(option)}
                    size='sm'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOptionClick(option)
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ConditionDeleteModal
